import { CloseIcon } from '@mantine/core';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Button, Popover, Stack } from 'src/core/ui';
import { useSimplifiedUndercutMode } from 'src/hooks/useSimplifiedUndercutMode';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ExpandIcon, FoldIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { SIMPLIFIED_UNDERCUT_MODE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { AutoPricingInputs, Feature } from 'src/WebApiController';

import * as styles from './CompAdvancedPricingSettingsContainer.css';
import { CompAdvancedPricingSettingsInclude } from './CompAdvancedPricingSettingsInclude';
import { CompAdvancedPricingSettingsUndercut } from './CompAdvancedPricingSettingsUndercut';

export const CompAdvancedPricingSettingsContainer = ({
  useVerticalView,
}: {
  useVerticalView: boolean | undefined;
}) => {
  const { watch, setValue } = useFormContext<AutoPricingInputs>();
  const {
    undercutMode,
    undercutReferenceListingPosition,
    compListingExcludeDefects,
    compListingExcludeFanInventory,
    compListingIncludeUnmappedInventory,
  } = watch();

  const hasUnmappedInvToggle = useUserHasFeature(Feature.UnmappedInvToggle);
  const defectsText = useContent(ContentId.Defects);
  const fanInventoryText = useContent(ContentId.FanInventory);
  const unmappedInventoryText = useContent(ContentId.UnmappedInventory);
  const includeValuesDisplayText = useMemo(() => {
    const arr = [];
    if (!compListingExcludeFanInventory) {
      arr.push(fanInventoryText);
    }
    if (!compListingExcludeDefects) {
      arr.push(defectsText);
    }
    // Only show if feature flag is enabled (positive naming - no inversion needed)
    if (hasUnmappedInvToggle && compListingIncludeUnmappedInventory) {
      arr.push(unmappedInventoryText);
    }

    return arr.join(', ');
  }, [
    compListingExcludeDefects,
    compListingExcludeFanInventory,
    compListingIncludeUnmappedInventory,
    defectsText,
    fanInventoryText,
    hasUnmappedInvToggle,
    unmappedInventoryText,
  ]);

  const { simplifiedUndercutMode, onSimplifiedUndercutModeChange } =
    useSimplifiedUndercutMode(
      undercutMode,
      undercutReferenceListingPosition,
      (newMode, newPosition) => {
        setValue('undercutMode', newMode);
        setValue('undercutReferenceListingPosition', newPosition);
      }
    );

  const [expandedSettings, setExpandedSettings] = useState<
    'Include' | 'Undercut'
  >('Include');

  const toggleExpandedSettings = useCallback(() => {
    setExpandedSettings((prev) =>
      prev === 'Include' ? 'Undercut' : 'Include'
    );
  }, []);

  return !useVerticalView ? (
    <div className={styles.advancedPricingSettingsContainer}>
      <CompAdvancedPricingSettingsInputDropdown
        title={ContentId.Include}
        valuesDisplayText={includeValuesDisplayText}
        popoverContent={<CompAdvancedPricingSettingsInclude />}
      />

      <CompAdvancedPricingSettingsInputDropdown
        title={ContentId.Undercut}
        valuesDisplayText={
          simplifiedUndercutMode && (
            <Content
              id={SIMPLIFIED_UNDERCUT_MODE_TO_CID[simplifiedUndercutMode]}
            />
          )
        }
        popoverContent={
          <CompAdvancedPricingSettingsUndercut
            simplifiedUndercutMode={simplifiedUndercutMode}
            onSimplifiedUndercutModeChange={onSimplifiedUndercutModeChange}
            undercutReferenceListingPosition={undercutReferenceListingPosition}
            setUndercutReferenceListingPosition={(newPosition) =>
              setValue('undercutReferenceListingPosition', newPosition)
            }
          />
        }
      />
    </div>
  ) : (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button
          variant="textPlain"
          style={{ padding: vars.spacing['xs'], whiteSpace: 'nowrap' }}
        >
          <div style={{ fontWeight: 400 }}>
            <Content id={ContentId.Advanced} />
          </div>
        </Button>
      </Popover.Trigger>
      <Popover.Content>
        <div className={styles.settingsVerticalViewPopover}>
          <div className={styles.settingsInputPopoverContentHeader}>
            <Content id={ContentId.AdvancedSettings} />

            <Popover.Close asChild>
              <CloseIcon size={vars.iconSize.m} />
            </Popover.Close>
          </div>
          <div className={styles.settingsInputPopoverContentBody}>
            <CompAdvancedPricingSettingsVerticalViewSection
              title={ContentId.Include}
              valuesDisplayText={includeValuesDisplayText}
              body={<CompAdvancedPricingSettingsInclude />}
              isExpanded={expandedSettings === 'Include'}
              toggleIsExpanded={toggleExpandedSettings}
            />

            <div className={styles.horizontalDivider} />

            <CompAdvancedPricingSettingsVerticalViewSection
              title={ContentId.Undercut}
              valuesDisplayText={
                simplifiedUndercutMode && (
                  <Content
                    id={SIMPLIFIED_UNDERCUT_MODE_TO_CID[simplifiedUndercutMode]}
                  />
                )
              }
              body={
                <CompAdvancedPricingSettingsUndercut
                  simplifiedUndercutMode={simplifiedUndercutMode}
                  onSimplifiedUndercutModeChange={
                    onSimplifiedUndercutModeChange
                  }
                  undercutReferenceListingPosition={
                    undercutReferenceListingPosition
                  }
                  setUndercutReferenceListingPosition={(newPosition) =>
                    setValue('undercutReferenceListingPosition', newPosition)
                  }
                />
              }
              isExpanded={expandedSettings === 'Undercut'}
              toggleIsExpanded={toggleExpandedSettings}
            />
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

const CompAdvancedPricingSettingsInputDropdown = ({
  title,
  valuesDisplayText,
  popoverContent,
}: {
  title: ContentId;
  valuesDisplayText: string | React.ReactNode;
  popoverContent: React.ReactNode;
}) => {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <div className={styles.settingsInputDropdown}>
          <div className={styles.settingsInputDropdownContent}>
            <div className={styles.settingsInputDropdownTitle}>
              <Content id={title} />
            </div>
            <div className={styles.settingsInputDropdownValuesDisplayText}>
              {valuesDisplayText}
            </div>
          </div>

          <ExpandIcon size={vars.iconSize.m} />
        </div>
      </Popover.Trigger>
      <Popover.Content>
        <div className={styles.settingsInputPopoverContent}>
          <div className={styles.settingsInputPopoverContentHeader}>
            <div>
              <Content id={title} />
            </div>

            <Popover.Close asChild>
              <CloseIcon size={vars.iconSize.m} />
            </Popover.Close>
          </div>
          <div className={styles.settingsInputPopoverContentBody}>
            {popoverContent}
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

const CompAdvancedPricingSettingsVerticalViewSection = ({
  title,
  valuesDisplayText,
  body,
  isExpanded,
  toggleIsExpanded,
}: {
  title: ContentId;
  valuesDisplayText: string | React.ReactNode;
  body: React.ReactNode;
  isExpanded: boolean;
  toggleIsExpanded: () => void;
}) => {
  return (
    <div className={styles.settingsInputPopoverContentBodySectionVerticalView}>
      <div
        className={styles.settingsInputDropdownVerticalView}
        onClick={toggleIsExpanded}
      >
        <Stack direction="row" gap="s">
          <div className={styles.settingsInputDropdownTitle}>
            <Content id={title} />
          </div>
          <div className={styles.settingsInputDropdownValuesDisplayText}>
            {valuesDisplayText}
          </div>
        </Stack>
        {isExpanded ? (
          <ExpandIcon size={vars.iconSize.m} />
        ) : (
          <FoldIcon size={vars.iconSize.m} />
        )}
      </div>

      {isExpanded && body}
    </div>
  );
};
